import React, { useState } from 'react';
import { useData } from '../data/DataProvider';
import { usePortalClient } from './usePortalClient';
import { date } from '../shared/formatters';
import { openProtectedFile, downloadProtectedFile, pbPortal } from '../lib/pb';
import { acknowledgementText } from '../lib/auditText';
import { mapAcknowledgement } from '../data/mappers';
import ConfirmDialog from '../shared/components/ConfirmDialog';
import { useToast } from '../shared/components/ToastProvider';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';
import EmptyState from '../shared/components/EmptyState';

const DAY = 86400000;

// Compliance state computed from expiry_date (never stored) — same
// green/amber/red contract as the admin CertificatesOverview (pass 5 §5F).
function certState(expiryDate) {
  if (!expiryDate) return { label: 'No expiry', pill: 'draft' };
  const days = Math.round((new Date(expiryDate) - Date.now()) / DAY);
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, pill: 'overdue' };
  if (days <= 30) return { label: `Expires in ${days}d`, pill: 'pending' };
  return { label: `Valid · ${days}d left`, pill: 'paid' };
}

// The stored version stamp is `v{n}:{filename}` (audit.pb.js) — ack state
// matches against the CURRENT document version only, so replacing the file
// (v2) legitimately requires a fresh acknowledgement.
const versionStampOf = (document) => `v${document.version || 1}:${document.filename}`;

// "View / download" mints a fresh ~2min file token AT CLICK TIME — never a
// URL baked into state at load time (that was the 404 bug; see pb.js).
export default function Documents() {
  const { data, updateCollection } = useData();
  const { client } = usePortalClient();
  const { notify } = useToast();
  const [opening, setOpening] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [ackTarget, setAckTarget] = useState(null); // document awaiting confirmation
  const [acking, setAcking] = useState(false);

  const documents = data.documents.filter((item) => item.client_id === client.id);
  const certificates = data.certificates
    .filter((item) => item.client_id === client.id)
    .sort((a, b) => new Date(a.expiry_date || 0) - new Date(b.expiry_date || 0));

  const myAcks = data.documentAcks.filter((item) => item.client_id === client.id);
  const ackFor = (document) =>
    myAcks.find((item) => item.document_id === document.id && item.document_version === versionStampOf(document));

  // Refresh the local ack state after a write (or a duplicate-400) so the
  // badge flips without a full data reload.
  const refreshAckState = async (documentId) => {
    try {
      const record = pbPortal.authStore.record;
      if (!record) return;
      const result = await pbPortal.collection('document_acknowledgements').getList(1, 50, {
        filter: `document_id="${documentId}" && user_id="${record.id}"`,
        sort: '-created',
      });
      const mapped = (result.items || []).map(mapAcknowledgement);
      updateCollection('documentAcks', (current) => [
        ...current.filter((item) => item.document_id !== documentId || item.user_id !== record.id),
        ...mapped,
      ]);
    } catch (err) {
      // Best-effort — the badge catches up on the next full load.
    }
  };

  // TRK-3.1: create sends ONLY document_id — the pb_hook forces identity,
  // scope and the canonical acknowledgement text. A 400 means "already
  // acknowledged" (hook throw or unique index) → re-check and treat as
  // success, exactly per the tracking_tasks.md contract.
  const acknowledge = async (document) => {
    setAcking(true);
    try {
      await pbPortal.collection('document_acknowledgements').create({ document_id: document.id });
      await refreshAckState(document.id);
      notify('Thank you — your confirmation has been recorded.');
    } catch (err) {
      const isDuplicate = err?.status === 400
        || /acknowledged/i.test(err?.message || '')
        || /Something went wrong/i.test(err?.message || '');
      if (isDuplicate) {
        await refreshAckState(document.id);
        notify('Thank you — your confirmation has been recorded.');
      } else {
        notify(err?.message || 'Could not record your confirmation', 'error');
      }
    } finally {
      setAcking(false);
      setAckTarget(null);
    }
  };

  const open = async (document) => {
    setOpening(document.id);
    try {
      await openProtectedFile(document, document.filename);
    } catch {
      // token failures surface as the browser's own error page; nothing to toast
    } finally {
      setOpening(null);
    }
  };

  const download = async (document) => {
    setDownloading(document.id);
    try {
      await downloadProtectedFile(document, document.filename);
    } catch {
      notify('Download failed — please try again', 'error');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      <div className="portal-title">
        <span className="eyebrow">Property record</span>
        <h1>Documents</h1>
        <p>Certificates, quotes, and photos for your property.</p>
      </div>
      {documents.length ? (
        <section className="portal-card document-grid">
          {documents.map((document) => {
            const ack = ackFor(document);
            return (
              <article className="document-card" key={document.id}>
                <SafeIcon icon={Icons.FileText} />
                <strong>{document.filename}</strong>
                <small>{date(document.uploaded_at)}{document.version > 1 ? ` · v${document.version}` : ''}</small>
                {document.requiresAcknowledgement && (
                  ack
                    ? <span className="status-pill status-completed"><span className="status-dot" />Acknowledged {date(ack.acknowledged_at)}</span>
                    : <span className="status-pill status-pending"><span className="status-dot" />Acknowledgement required</span>
                )}
                {document.filename && document.filename !== 'document' ? (
                  <div className="document-actions">
                    <button className="link-button" onClick={() => open(document)} disabled={opening === document.id}>
                      {opening === document.id ? 'Opening…' : 'View'}
                    </button>
                    <button className="link-button" onClick={() => download(document)} disabled={downloading === document.id}>
                      {downloading === document.id ? 'Downloading…' : 'Download'}
                    </button>
                    {document.requiresAcknowledgement && !ack && (
                      <button className="link-button" onClick={() => setAckTarget(document)}>Acknowledge</button>
                    )}
                  </div>
                ) : (
                  <span>Unavailable</span>
                )}
              </article>
            );
          })}
        </section>
      ) : (
        <EmptyState title="No documents yet" />
      )}

      <div className="portal-section-head">
        <div>
          <span className="eyebrow">Compliance</span>
          <h2>Certificates</h2>
        </div>
      </div>
      {certificates.length ? (
        <section className="portal-card no-padding invoice-list">
          {certificates.map((cert) => {
            const state = certState(cert.expiry_date);
            return (
              <div className="invoice-row" key={cert.id}>
                <div>
                  <strong>{cert.certificate_number || cert.typeLabel}</strong>
                  <span>{cert.typeLabel}</span>
                </div>
                <span>Expires {date(cert.expiry_date)}</span>
                <span className={`status-pill status-${state.pill}`}><span className="status-dot" />{state.label}</span>
                {cert.filename ? (
                  <button className="link-button" onClick={() => open(cert)} disabled={opening === cert.id}>
                    {opening === cert.id ? 'Opening…' : 'View'}
                  </button>
                ) : <span>No file</span>}
              </div>
            );
          })}
        </section>
      ) : (
        <EmptyState title="No certificates yet" />
      )}

      <ConfirmDialog
        open={Boolean(ackTarget)}
        title="Acknowledge this document"
        message={ackTarget
          ? `"${ackTarget.filename}" (version ${ackTarget.version || 1})\n\n${ackmentSentence(ackTarget)}`
          : ''}
        confirmLabel="Confirm acknowledgement"
        onClose={() => setAckTarget(null)}
        onConfirm={() => ackTarget && acknowledge(ackTarget)}
        busy={acking}
      />
    </>
  );
}

// The EXACT sentence the server stores (audit.pb.js) — the modal previews
// it verbatim so the client knows precisely what they are confirming.
function ackmentSentence(document) {
  return acknowledgementText(document.filename, document.version || 1);
}
